/**
 * Price movement between two published sheets. Powers the publish confirm gate
 * and the per-edition diff on /admin/prices.
 *
 * Deltas are computed in integer CENTS and converted back once. Subtracting two
 * dollar doubles is how a price-movement column acquires -0.009999999998; this
 * is the one place that risk exists, so it is contained here rather than left to
 * every caller to remember.
 */
import type { PriceSheet } from './editionSheet';
import { centsToDollars } from './money';

export interface DiffRow {
  productSku: string;
  quarter: string;
  was: number | null;
  now: number | null;
  deltaDollars: number;
}

const toCents = (dollars: number): number => Math.round(dollars * 100);

export function diffSheets(prev: PriceSheet | null, next: PriceSheet): DiffRow[] {
  const rows: DiffRow[] = [];
  const skus = new Set([...Object.keys(prev ?? {}), ...Object.keys(next)]);

  for (const productSku of [...skus].sort()) {
    const prevPrices = prev?.[productSku]?.prices ?? {};
    const nextPrices = next[productSku]?.prices ?? {};
    const quarters = new Set([...Object.keys(prevPrices), ...Object.keys(nextPrices)]);

    for (const quarter of [...quarters].sort()) {
      const was = prevPrices[quarter] ?? null;
      const now = nextPrices[quarter] ?? null;
      if (was === now) continue;
      const deltaCents = toCents(now ?? 0) - toCents(was ?? 0);
      rows.push({ productSku, quarter, was, now, deltaDollars: centsToDollars(deltaCents) });
    }
  }
  return rows;
}
